import React, {useEffect, useState} from "react";
import {DragDropContext, Droppable} from "react-beautiful-dnd";
import YuJaButton from "../../components/standardization/YuJaButton";
import {ReactComponent as PlusIcon} from "../../images/plus.svg";
import {GRADE_SCHEMA} from "../../utils/constants";
import {
    findFirstOverflowParent,
    intToChar,
    notifyInfo, questionHasCorrectness,
    reorderKeysByDragDrop,
    send2ScreenReader
} from "../../utils/helpers";
import {addOptionToQuestion} from "../../utils/questionUtils";
import "../styles.css";
import {useCheckMainContainerNarrow} from "../../hooks/useCheckMainContainerNarrow";
import {YuJaCheckbox} from "../../components/standardization/YuJaCheckbox";
import classes from "../questions.module.css";
import MCQuestionOptionNew from "./MCQuestionOptionNew";
import { useTranslation } from "react-i18next";

export default function MCQuestionNew({
                                          gradeSchema=GRADE_SCHEMA.GRADED.key,
                                          setQuestions,
                                          questions,
                                          selectedQuestion,
                                          getPollKey,
                                          questionBankId,
                                      }) {
    const { t } = useTranslation();
    const [warningShowed, setWarningShowed] = useState(false);
    const {optionsMap, serialNo} = selectedQuestion;
    const {isNarrow} = useCheckMainContainerNarrow();

    const initializeCollapse = () => {
        const res = {};
        for (const key of Object.keys(optionsMap)) {
            res[key] = true;
        }
        return res;
    }

    const [collapseStatusObj, setCollapseStatusObj] = useState(() => initializeCollapse());

    useEffect(() => {
        setCollapseStatusObj(initializeCollapse);
    }, [serialNo]);


    useEffect(() => {
        if (Object.keys(optionsMap).length > Object.keys(collapseStatusObj).length) {
            let copy = {};
            for (const key of Object.keys(optionsMap)) {
                copy[key] = collapseStatusObj.hasOwnProperty(key) ? collapseStatusObj[key] : true;
            }
            setCollapseStatusObj(copy);
        }
    }, [optionsMap]);

    const handleAnswerOrderChange = (result) => {
        if (!result.destination || result.source.index === result.destination.index) {
            return;
        }


        let fromIndex = result.source.index;
        let toIndex = result.destination.index;


        let newQuestions = [];
        let copy = {};
        questions.forEach((q) => {
            if (q.serialNo !== serialNo) {
                newQuestions.push(q);
            } else {
                const questionCopy = JSON.parse(JSON.stringify(q));
                let reorderKeys = reorderKeysByDragDrop(fromIndex, toIndex, Object.keys(questionCopy.optionsMap));

                let newOptionsMap = {};
                let newCorrectAnswers = [];
                let newOptionsImageMap = {};
                for (let i = 0; i < reorderKeys.length; i++) {
                    const key = intToChar(i);
                    newOptionsMap[key] = questionCopy.optionsMap[reorderKeys[i]];
                    copy[key] = collapseStatusObj[reorderKeys[i]];
                    if (questionCopy.correctAnswers.includes(reorderKeys[i])) {
                        newCorrectAnswers.push(key);
                    }

                    if (!!q.optionsImageMap && !!q.optionsImageMap.hasOwnProperty(reorderKeys[i])) {
                        newOptionsImageMap[key] = q.optionsImageMap[reorderKeys[i]];
                    }
                }

                questionCopy.optionsMap = newOptionsMap;
                questionCopy.correctAnswers = newCorrectAnswers.sort();
                questionCopy.optionsImageMap = newOptionsImageMap;
                newQuestions.push(questionCopy);
            }
        });

        setCollapseStatusObj(copy);
        setQuestions(newQuestions);
    };

    const handleCorrectAnyCheck = () => {
        let newQuestions = [];
        questions.forEach((q) => {
            if (q.serialNo !== serialNo) {
                newQuestions.push(q);
            } else {
                const questionCopy = JSON.parse(JSON.stringify(q));
                questionCopy.correctAnyEnable = !Boolean(questionCopy.correctAnyEnable);
                newQuestions.push(questionCopy);
            }
        });
        setQuestions(newQuestions);
    };


    return (
        <div className={classes.optionsContainer}>
            {!!selectedQuestion.correctAnswers && selectedQuestion.correctAnswers.length > 1 && questionHasCorrectness(selectedQuestion) &&
                <YuJaCheckbox
                    checked={!!selectedQuestion.correctAnyEnable}
                    onChange={handleCorrectAnyCheck}
                    label={t("poll.correctAnyDescription")}
                />
            }

            <DragDropContext onDragEnd={(result) => handleAnswerOrderChange(result)}>
                <Droppable droppableId={`mc-question-${serialNo}-option-droppable-list`}>
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{display: "flex", flexDirection: "column", gap: 12}}
                        >
                            {!!optionsMap && typeof optionsMap === 'object' &&
                                Object.entries(optionsMap).map(([choice, optionValue], index) => {
                                    return (
                                        <MCQuestionOptionNew
                                            gradeSchema={gradeSchema}
                                            getPollKey={getPollKey}
                                            optionData={{choice: choice, optionValue: optionValue}}
                                            selectedQuestion={selectedQuestion}
                                            questions={questions}
                                            setQuestions={setQuestions}
                                            index={index}
                                            key={index}
                                            questionBankId={questionBankId}
                                            maxSize={Object.entries(optionsMap).length}
                                            collapsed={!collapseStatusObj.hasOwnProperty(choice) || collapseStatusObj[choice]}
                                            setCollapsed={(status) => {
                                                let copy = JSON.parse(JSON.stringify(collapseStatusObj));
                                                copy[choice] = status;
                                                setCollapseStatusObj(copy);
                                            }}

                                            deleteCollapsed={() => {
                                                let copy = {};
                                                Object.entries(collapseStatusObj).forEach(([key, value]) => {
                                                    if (key !== choice) {
                                                        if (key > choice) {
                                                            const newKey = String.fromCharCode(key.charCodeAt(0) - 1);
                                                            copy[newKey] = value;
                                                        } else {
                                                            copy[key] = value;
                                                        }
                                                    }
                                                });
                                                setCollapseStatusObj(copy);
                                            }}


                                        />
                                    );
                                })

                            }
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            <YuJaButton
                title={t("poll.mcssAddOptionText")}
                style={{
                    width: 170,
                    height: 35,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    alignSelf: "end"
                }}
                onClick={(e) => {
                    const newQuestions = addOptionToQuestion(
                        selectedQuestion,
                        questions
                    );
                    if (newQuestions) {
                        setQuestions(newQuestions);
                        send2ScreenReader(`New option is added.`);
                        let currentTarget = e.currentTarget;
                        setTimeout(() => {
                            const parent = findFirstOverflowParent(currentTarget);
                            parent?.scroll({top: parent.scrollHeight, behavior: "smooth"});
                        }, 100);
                    } else if (!warningShowed) {
                        setWarningShowed(true);
                        notifyInfo(t("questionBuilder.maxOptionsReachedInfo"));
                    }
                }}
            >
                <PlusIcon
                    style={{width: 15, height: 15, marginRight: 2}}/> {t("poll.mcssAddOptionText")}
            </YuJaButton>
        </div>
    );
}
